import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = {
  status: ['#FFCE56', '#36A2EB', '#4BC0C0', '#9966FF', '#22C55E', '#EF4444', '#F97316'],
  payment: ['#F97316', '#22C55E', '#3B82F6'],
  revenue: '#F97316',
  product: '#22C55E'
};

const ReportsPanel = ({ orders, products, customers }) => {
  // Calculate metrics
  const metrics = useMemo(() => {
    if (!orders || orders.length === 0) {
      return {
        totalRevenue: 0,
        totalOrders: 0,
        avgOrderValue: 0,
        pendingOrders: 0,
        completedOrders: 0,
        totalCustomers: customers?.length || 0
      };
    }

    const totalRevenue = orders.reduce((sum, order) => {
      const totalAmountRaw = order.total_amount || 0;
      const isNewFormat = totalAmountRaw >= 10000;
      const total = isNewFormat ? (totalAmountRaw / 100) : totalAmountRaw;
      return sum + total;
    }, 0);

    const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'confirmed').length;
    const completedOrders = orders.filter(o => o.status === 'completed' || o.status === 'delivered').length;

    return {
      totalRevenue,
      totalOrders: orders.length,
      avgOrderValue: orders.length > 0 ? totalRevenue / orders.length : 0,
      pendingOrders,
      completedOrders,
      totalCustomers: customers?.length || 0
    };
  }, [orders, customers]);

  // Order Status Distribution (Pie Chart)
  const orderStatusData = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    const statusCounts = orders.reduce((acc, order) => {
      const status = order.status || 'pending';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const statusMap = {
      'pending': 'Pending',
      'confirmed': 'Confirmed',
      'shipped': 'Shipped',
      'delivered': 'Delivered',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
      'returned': 'Returned'
    };

    return Object.entries(statusCounts).map(([key, value]) => ({
      name: statusMap[key] || key,
      value
    }));
  }, [orders]);

  // Payment Method Distribution (Pie Chart)
  const paymentMethodData = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    const methodCounts = orders.reduce((acc, order) => {
      const method = order.payment_method || 'cash';
      acc[method] = (acc[method] || 0) + 1;
      return acc;
    }, {});

    const methodMap = {
      'cash': 'Cash on Delivery',
      'bkash': 'bKash',
      'nagad': 'Nagad'
    };

    return Object.entries(methodCounts).map(([key, value]) => ({
      name: methodMap[key] || key,
      value
    }));
  }, [orders]);

  // Revenue Trend (Line Chart)
  const revenueTrendData = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    const dailyRevenue = orders.reduce((acc, order) => {
      const date = new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const totalAmountRaw = order.total_amount || 0;
      const isNewFormat = totalAmountRaw >= 10000;
      const total = isNewFormat ? (totalAmountRaw / 100) : totalAmountRaw;
      
      if (total > 0) {
        acc[date] = (acc[date] || 0) + total;
      }
      return acc;
    }, {});

    return Object.entries(dailyRevenue)
      .map(([date, revenue]) => ({ date, revenue: parseFloat(revenue.toFixed(2)) }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-30); // Last 30 days
  }, [orders]);

  // Product Sales (Bar Chart)
  const productSalesData = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    const productSales = {};
    
    orders.forEach(order => {
      if (order.order_items && order.order_items.length > 0) {
        order.order_items.forEach(item => {
          const productName = item.products?.name_bn || item.products?.name_en || 'Unknown Product';
          productSales[productName] = (productSales[productName] || 0) + item.quantity;
        });
      }
    });

    return Object.entries(productSales)
      .map(([name, quantity]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        sales: quantity
      }))
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 10); // Top 10 products
  }, [orders]);

  // Custom tooltip for currency
  const CurrencyTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800">{payload[0].name || payload[0].payload.date}</p>
          <p className="text-orange-600 font-bold">৳{payload[0].value.toFixed(2)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 shadow-lg"
          style={{ boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <div className="text-blue-600 text-sm font-semibold mb-1">Total Revenue</div>
          <div className="text-2xl font-bold text-blue-900">৳{metrics.totalRevenue.toFixed(2)}</div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200 shadow-lg"
          style={{ boxShadow: '0 4px 20px rgba(34, 197, 94, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <div className="text-green-600 text-sm font-semibold mb-1">Total Orders</div>
          <div className="text-2xl font-bold text-green-900">{metrics.totalOrders}</div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200 shadow-lg"
          style={{ boxShadow: '0 4px 20px rgba(249, 115, 22, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <div className="text-orange-600 text-sm font-semibold mb-1">Avg Order Value</div>
          <div className="text-2xl font-bold text-orange-900">৳{metrics.avgOrderValue.toFixed(2)}</div>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200 shadow-lg"
          style={{ boxShadow: '0 4px 20px rgba(255, 206, 86, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <div className="text-yellow-600 text-sm font-semibold mb-1">Pending Orders</div>
          <div className="text-2xl font-bold text-yellow-900">{metrics.pendingOrders}</div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200 shadow-lg"
          style={{ boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <div className="text-emerald-600 text-sm font-semibold mb-1">Completed Orders</div>
          <div className="text-2xl font-bold text-emerald-900">{metrics.completedOrders}</div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200 shadow-lg"
          style={{ boxShadow: '0 4px 20px rgba(168, 85, 247, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <div className="text-purple-600 text-sm font-semibold mb-1">Total Customers</div>
          <div className="text-2xl font-bold text-purple-900">{metrics.totalCustomers}</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status - Pie Chart */}
        <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-6"
          style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <h3 className="text-xl font-bold text-slate-800 mb-4">Order Status Distribution</h3>
          {orderStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={orderStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={800}
                  strokeWidth={2}
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS.status[index % COLORS.status.length]}
                      stroke="#fff"
                    />
                  ))}
                </Pie>
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                          <p className="font-semibold text-gray-800">{payload[0].name}</p>
                          <p className="text-blue-600 font-bold">{payload[0].value} orders</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => <span className="text-sm font-medium">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">No data available</div>
          )}
        </div>

        {/* Payment Methods - Pie Chart (Doughnut Style) */}
        <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-6"
          style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <h3 className="text-xl font-bold text-slate-800 mb-4">Payment Methods</h3>
          {paymentMethodData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={paymentMethodData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  fill="#8884d8"
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={800}
                  strokeWidth={2}
                >
                  {paymentMethodData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS.payment[index % COLORS.payment.length]}
                      stroke="#fff"
                    />
                  ))}
                </Pie>
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                          <p className="font-semibold text-gray-800">{payload[0].name}</p>
                          <p className="text-green-600 font-bold">{payload[0].value} orders</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => <span className="text-sm font-medium">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">No data available</div>
          )}
        </div>

        {/* Revenue Trend - Line Chart */}
        <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-6 lg:col-span-2"
          style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <h3 className="text-xl font-bold text-slate-800 mb-4">Revenue Trend (Last 30 Days)</h3>
          {revenueTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={revenueTrendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `৳${value}`}
                />
                <Tooltip content={<CurrencyTooltip />} />
                <Legend 
                  formatter={(value) => <span className="text-sm font-medium">{value}</span>}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke={COLORS.revenue} 
                  strokeWidth={3}
                  dot={{ fill: COLORS.revenue, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8 }}
                  name="Daily Revenue"
                  animationDuration={800}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">No data available</div>
          )}
        </div>

        {/* Product Sales - Bar Chart */}
        <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-6 lg:col-span-2"
          style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
          <h3 className="text-xl font-bold text-slate-800 mb-4">Top 10 Products by Sales</h3>
          {productSalesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={productSalesData} 
                margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis 
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Units Sold', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                          <p className="font-semibold text-gray-800">{payload[0].payload.name}</p>
                          <p className="text-green-600 font-bold">{payload[0].value} units sold</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  formatter={(value) => <span className="text-sm font-medium">{value}</span>}
                />
                <Bar 
                  dataKey="sales" 
                  fill={COLORS.product}
                  stroke={COLORS.product}
                  strokeWidth={2}
                  radius={[8, 8, 0, 0]}
                  name="Units Sold"
                  animationDuration={800}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">No data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPanel;